import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { deleteConfigSetting } from 'api/Admin/DevSettings';

import { pm } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import state from 'components/state';

import FeatureFlagModal from '../../components/Modal';

const ActionCellRenderer = ({ data, node }) => {
  const permissions = useRecoilValue(state.permissions);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteMutation = useMutation({
    mutationFn: (payload) => deleteConfigSetting(navigate, payload),
    onSuccess: (res) => {
      if (res?.code === 0) {
        showAlert({ title: 'Feature flag deleted successfully.', color: 'success' });
        queryClient.invalidateQueries(['featureFlags']);
      } else {
        showAlert({ title: res?.error ?? 'Failed to delete feature flag.', color: 'danger' });
      }
    },
    onError: () => {
      showAlert({ title: 'Failed to delete feature flag.', color: 'danger' });
    }
  });

  if (node?.group) return null;

  const options = [
    ...(pm(permissions, 'admin_feature_flag.update')
      ? [
          {
            label: 'Edit',
            icon: 'edit-2-mono',
            onClick: () => setShowEditModal(true)
          }
        ]
      : []),
    ...(pm(permissions, 'admin_feature_flag.delete')
      ? [
          {
            label: 'Delete',
            icon: 'trash-mono',
            onClick: () => setShowDeleteConfirm(true)
          }
        ]
      : [])
  ];

  if (options.length === 0) return null;

  return (
    <>
      <Popover
        className="mt-2 flex flex-1 justify-center self-center"
        position="left"
        isFixed
        isBorder={false}
        options={options}
      />

      <Confirm
        variant="danger"
        primaryBtnTxt="Delete"
        title="Delete Feature Flag"
        icon="trash-mono"
        message={`Are you sure you want to delete "${data.key}"?`}
        handleOpen={showDeleteConfirm}
        handleClose={() => setShowDeleteConfirm(false)}
        handleContinue={() => {
          deleteMutation.mutate({ key: `featureFlags.${data.key}` });
          setShowDeleteConfirm(false);
        }}
      />

      {showEditModal && (
        <FeatureFlagModal
          isOpen={showEditModal}
          handleClose={() => setShowEditModal(false)}
          item={data}
        />
      )}
    </>
  );
};

export default ActionCellRenderer;
